import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Car,
  Plus,
  Edit,
  Trash2,
  LogOut,
  MoreVertical,
  Check,
  X,
  ExternalLink,
  Home,
  Upload,
  Image,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
};

const emptyVehicle = {
  make: "",
  model: "",
  year: new Date().getFullYear(),
  color: "",
  image_url: "",
  description: "",
  features: [],
  available: true,
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formData, setFormData] = useState(emptyVehicle);
  const [featuresInput, setFeaturesInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchVehicles();
  }, [navigate]);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/all`);
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    toast.success("Logged out successfully");
    navigate("/admin");
  };

  const openAddDialog = () => {
    setSelectedVehicle(null);
    setFormData(emptyVehicle);
    setFeaturesInput("");
    setDialogOpen(true);
  };

  const openEditDialog = (vehicle) => {
    setSelectedVehicle(vehicle);
    setFormData({
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      color: vehicle.color,
      image_url: vehicle.image_url,
      description: vehicle.description || "",
      features: vehicle.features || [],
      available: vehicle.available,
    });
    setFeaturesInput((vehicle.features || []).join(", "));
    setDialogOpen(true);
  };

  const openDeleteDialog = (vehicle) => {
    setSelectedVehicle(vehicle);
    setDeleteDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a JPG, PNG, or WebP image.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB.");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      // Set the returned image URL
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`;
      setFormData({ ...formData, image_url: imageUrl });
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        features: featuresInput
          .split(",")
          .map((f) => f.trim())
          .filter((f) => f),
      };

      if (selectedVehicle) {
        await axios.put(
          `${API}/admin/vehicles/${selectedVehicle.id}`,
          dataToSend,
          { headers: getAuthHeaders() }
        );
        toast.success("Vehicle updated successfully!");
      } else {
        await axios.post(`${API}/admin/vehicles`, dataToSend, {
          headers: getAuthHeaders(),
        });
        toast.success("Vehicle added successfully!");
      }
      setDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vehicle");
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/vehicles/${selectedVehicle.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Vehicle deleted successfully!");
      setDeleteDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete vehicle");
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar fixed left-0 top-0 h-full hidden lg:block">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-heading font-bold text-sm tracking-tight">
                Easy Payments
              </span>
              <span className="block text-xs text-slate-500 uppercase tracking-widest">
                Admin
              </span>
            </div>
          </div>
        </div>

        <nav className="mt-6">
          <button className="admin-sidebar-link active w-full text-left">
            <Car className="w-5 h-5" />
            Vehicles
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <a
            href="/"
            className="admin-sidebar-link mb-2"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Home className="w-5 h-5" />
            View Site
            <ExternalLink className="w-4 h-4 ml-auto" />
          </a>
          <button
            onClick={handleLogout}
            className="admin-sidebar-link w-full text-left text-red-400 hover:text-red-300 hover:bg-red-900/20"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 lg:hidden">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold">Admin</span>
            </div>
            <h1 className="font-heading font-bold text-xl text-slate-900 hidden lg:block">
              Vehicle Management
            </h1>
            <Button
              onClick={openAddDialog}
              className="bg-primary text-white uppercase tracking-wide font-bold btn-brutalist"
              data-testid="add-vehicle-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Vehicle
            </Button>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="bg-white border border-slate-200 p-8">
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-slate-100"></div>
                ))}
              </div>
            </div>
          ) : vehicles.length === 0 ? (
            <div className="bg-white border border-slate-200 p-12 text-center">
              <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">
                No Vehicles Yet
              </h2>
              <p className="text-slate-500 mb-6">
                Add your first vehicle to get started.
              </p>
              <Button
                onClick={openAddDialog}
                className="bg-primary text-white"
                data-testid="add-first-vehicle-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Button>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 overflow-hidden">
              <table className="admin-table" data-testid="vehicles-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Year</th>
                    <th>Color</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`}>
                      <td>
                        <div className="flex items-center gap-4">
                          <img
                            src={vehicle.image_url}
                            alt={`${vehicle.make} ${vehicle.model}`}
                            className="w-16 h-12 object-cover"
                          />
                          <div>
                            <span className="font-bold text-slate-900">
                              {vehicle.make} {vehicle.model}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="font-mono">{vehicle.year}</td>
                      <td>{vehicle.color}</td>
                      <td>
                        {vehicle.available ? (
                          <span className="badge-available">
                            <Check className="w-3 h-3 mr-1" />
                            Available
                          </span>
                        ) : (
                          <span className="badge-unavailable">
                            <X className="w-3 h-3 mr-1" />
                            Unavailable
                          </span>
                        )}
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`vehicle-actions-${vehicle.id}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => openEditDialog(vehicle)}
                              data-testid={`edit-vehicle-${vehicle.id}`}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openDeleteDialog(vehicle)}
                              className="text-red-600"
                              data-testid={`delete-vehicle-${vehicle.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              {selectedVehicle ? "Edit Vehicle" : "Add New Vehicle"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="make">Make</Label>
                <Input
                  id="make"
                  value={formData.make}
                  onChange={(e) =>
                    setFormData({ ...formData, make: e.target.value })
                  }
                  placeholder="Toyota"
                  className="mt-1"
                  data-testid="input-make"
                />
              </div>
              <div>
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) =>
                    setFormData({ ...formData, model: e.target.value })
                  }
                  placeholder="Camry"
                  className="mt-1"
                  data-testid="input-model"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="year">Year</Label>
                <Input
                  id="year"
                  type="number"
                  value={formData.year}
                  onChange={(e) =>
                    setFormData({ ...formData, year: parseInt(e.target.value) })
                  }
                  className="mt-1"
                  data-testid="input-year"
                />
              </div>
              <div>
                <Label htmlFor="color">Color</Label>
                <Input
                  id="color"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  placeholder="Silver"
                  className="mt-1"
                  data-testid="input-color"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="image">Vehicle Photo</Label>
              <div className="mt-2 space-y-3">
                {/* Upload Button */}
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="image-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white cursor-pointer hover:bg-primary/90 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    {uploading ? "Uploading..." : "Choose Photo"}
                  </label>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/jpg"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="image-upload-input"
                  />
                  <span className="text-sm text-slate-500">or paste URL below</span>
                </div>
                
                {/* URL Input */}
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  placeholder="https://... or upload above"
                  data-testid="input-image-url"
                />
                
                {/* Preview */}
                {formData.image_url && (
                  <div className="relative w-full h-32 bg-slate-100 border border-slate-200 overflow-hidden">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Brief description of the vehicle..."
                className="mt-1"
                rows={3}
                data-testid="input-description"
              />
            </div>

            <div>
              <Label htmlFor="features">Features (comma-separated)</Label>
              <Input
                id="features"
                value={featuresInput}
                onChange={(e) => setFeaturesInput(e.target.value)}
                placeholder="Automatic, AC, Bluetooth"
                className="mt-1"
                data-testid="input-features"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label htmlFor="available" className="cursor-pointer">
                Available for rent
              </Label>
              <Switch
                id="available"
                checked={formData.available}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, available: checked })
                }
                data-testid="switch-available"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.make || !formData.model}
              className="bg-primary text-white"
              data-testid="save-btn"
            >
              {saving ? "Saving..." : selectedVehicle ? "Update" : "Add Vehicle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              Delete Vehicle?
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 py-4">
            Are you sure you want to delete{" "}
            <strong>
              {selectedVehicle?.make} {selectedVehicle?.model}
            </strong>
            ? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
